import { createContext, useContext, useMemo, ReactNode } from 'react';
import { ExecutionResponse, StepExecutionResponse } from '../types';

export const INBOUND_NODE_ID = '__inbound__';

export interface ExecutionViewState {
  execution: ExecutionResponse;
  steps: StepExecutionResponse[];
  stepByNodeId: Map<string, StepExecutionResponse>;
}

interface ExecutionContextValue {
  primary: ExecutionViewState | null;
  compare: ExecutionViewState | null;
}

const ExecutionViewContext = createContext<ExecutionContextValue>({
  primary: null,
  compare: null,
});

interface ProviderProps {
  primary: { execution: ExecutionResponse; steps: StepExecutionResponse[] } | null;
  compare: { execution: ExecutionResponse; steps: StepExecutionResponse[] } | null;
  children: ReactNode;
}

function buildState(
  v: { execution: ExecutionResponse; steps: StepExecutionResponse[] } | null,
): ExecutionViewState | null {
  if (!v) return null;
  const map = new Map<string, StepExecutionResponse>();
  for (const s of v.steps) map.set(s.nodeId, s);
  return { execution: v.execution, steps: v.steps, stepByNodeId: map };
}

export function ExecutionViewProvider({ primary, compare, children }: ProviderProps) {
  const value = useMemo<ExecutionContextValue>(
    () => ({ primary: buildState(primary), compare: buildState(compare) }),
    [primary, compare],
  );
  return (
    <ExecutionViewContext.Provider value={value}>
      {children}
    </ExecutionViewContext.Provider>
  );
}

export function useExecutionView() {
  return useContext(ExecutionViewContext);
}

export function buildStepOutputsMap(
  steps: StepExecutionResponse[],
  aliasByNodeId: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of steps) {
    if (!s.output) continue;
    out[s.nodeId] = s.output;
    const alias = aliasByNodeId[s.nodeId];
    if (alias) out[alias] = s.output;
  }
  return out;
}

interface InboundContext {
  body: string;
  headers: Record<string, string>;
  query: Record<string, string>;
}

export function extractInboundFromSteps(
  execution: ExecutionResponse,
  steps: StepExecutionResponse[],
): InboundContext {
  const fallback: InboundContext = {
    body: execution.inboundPayload,
    headers: {},
    query: {},
  };
  if (steps.length === 0) return fallback;
  try {
    const first = JSON.parse(steps[0].input);
    return {
      body: typeof first.BodyJson === 'string' ? first.BodyJson : execution.inboundPayload,
      headers: first.Headers ?? {},
      query: first.Query ?? {},
    };
  } catch {
    return fallback;
  }
}
